import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import CommonLayout from "../../layouts/CommonLayout";
import StaticKakaoMapContainer from '../../containers/StaticKakaoMapContainer';
import PlaceSelectionContainer from '../../containers/PlaceSelectionContainer';
import { getMeetingById, updateMeeting } from '../../../api/MeetingApi';
import useLoadingStore from '../../../store/loadingStore';
import { IoLocationOutline, IoChevronForward } from 'react-icons/io5';
import { AlertModal } from '../../layouts/AlertModal';

const MeetingUpdatePage = () => {
    const { meetingId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setIsLoading } = useLoadingStore();

    const [meetingData, setMeetingData] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
    });
    const [placeData, setPlaceData] = useState({
        placeName: '',
        placeAddress: '',
        placePhone: '',
        placeLatitude: null,
        placeLongitude: null,
        partnerPlaceId: null,
    });
    const [showPlaceSelection, setShowPlaceSelection] = useState(false);

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    useEffect(() => {
        const loadMeetingData = async () => {
            try {
                setIsLoading(true);
                const response = await getMeetingById(meetingId);
                setMeetingData(response);

                setFormData({
                    title: response.title || '',
                    content: response.content || '',
                });

                setPlaceData({
                    placeName: response.placeName || '',
                    placeAddress: response.placeAddress || '',
                    placePhone: response.placePhone || '',
                    placeLatitude: response.placeLatitude,
                    placeLongitude: response.placeLongitude,
                    partnerPlaceId: response.partnerPlaceId,
                });
            } catch (error) {
                console.error('모임 데이터 로드 실패:', error);
                setAlertState({
                    isVisible: true,
                    message: '모임 정보를 불러오는 중 오류가 발생했습니다.',
                    callback: () => navigate(-1)
                });
            } finally {
                setIsLoading(false);
            }
        };
        loadMeetingData();
    }, [meetingId, setIsLoading, navigate]);

    useEffect(() => {
        if (location.state?.selectedPlace) {
            const place = location.state.selectedPlace;
            setPlaceData({
                placeName: place.name,
                placeAddress: place.address,
                placePhone: place.phone || '',
                placeLatitude: place.latitude,
                placeLongitude: place.longitude,
                partnerPlaceId: place.isPartner ? place.partnerId : null,
            });
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handlePlaceSelect = () => {
        setShowPlaceSelection(true);
    };

    const handlePlaceSelectionSelect = (place) => {
        setPlaceData({
            placeName: place.name,
            placeAddress: place.address,
            placePhone: place.phone || '',
            placeLatitude: place.latitude,
            placeLongitude: place.longitude,
            partnerPlaceId: place.isPartner ? place.partnerId : null,
        });
        setShowPlaceSelection(false);
    };

    const handlePlaceSelectionBack = () => {
        setShowPlaceSelection(false);
    };

    const isFormValid = () => {
        return !!(
            formData.title.trim() &&
            formData.content.trim() &&
            placeData.placeName.trim() &&
            placeData.placeAddress.trim() &&
            placeData.placeLatitude &&
            placeData.placeLongitude
        );
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleSave = async () => {
        if (!isFormValid()) {
            setAlertState({ isVisible: true, message: '모든 필수 항목을 입력해주세요.' });
            return;
        }

        try {
            setIsLoading(true);
            const updateData = {
                title: formData.title.trim(),
                content: formData.content.trim(),
                placeName: placeData.placeName.trim(),
                placeAddress: placeData.placeAddress.trim() || null,
                placePhone: placeData.placePhone.trim() || null,
                placeLatitude: placeData.placeLatitude,
                placeLongitude: placeData.placeLongitude,
                partnerPlaceId: placeData.partnerPlaceId,
            };

            await updateMeeting(meetingId, updateData);
            setAlertState({
                isVisible: true,
                message: '모임 정보가 수정되었습니다!',
                callback: () => navigate(-1)
            });
        } catch (error) {
            console.error('모임 수정 실패:', error);
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    if (showPlaceSelection) {
        return (
            <PlaceSelectionContainer
                title="장소 변경"
                onPlaceSelect={handlePlaceSelectionSelect}
                onBack={handlePlaceSelectionBack}
                previousPlace={placeData.placeName ? {
                    name: placeData.placeName,
                    address: placeData.placeAddress,
                    phone: placeData.placePhone,
                    latitude: placeData.placeLatitude,
                    longitude: placeData.placeLongitude,
                    isPartner: !!placeData.partnerPlaceId,
                    partnerId: placeData.partnerPlaceId
                } : undefined}
            />
        );
    }

    if (!meetingData) {
        return (
            <CommonLayout title="모임 수정">
                <AlertModal
                    isVisible={alertState.isVisible}
                    message={alertState.message}
                    callback={alertState.callback}
                    onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
                />
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">모임 정보를 불러오는 중...</p>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout
            title="모임 수정"
            showBackButton={false}
        >
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <div className="max-w-md mx-auto bg-white">
                <div className="p-4 space-y-6">
                    {/* 제목 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">제목</label>
                        <input
                            type="text"
                            placeholder="모임 제목을 입력해주세요 (최대 20자)"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            maxLength={20}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        <p className="text-xs text-gray-500 text-right mt-1">{formData.title.length}/20</p>
                    </div>

                    {/* 설명 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
                        <textarea
                            placeholder="모임에 대한 설명을 입력해주세요 (최대 300자)"
                            value={formData.content}
                            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                            maxLength={300}
                            rows={4}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                        />
                        <p className="text-xs text-gray-500 text-right mt-1">{formData.content.length}/300</p>
                    </div>

                    {/* 모임 장소 (수정 가능) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            모임 장소
                        </label>
                        {placeData.placeName ? (
                            <div className="space-y-3">
                                {/* 정적 지도 */}
                                <div className="w-full aspect-[2/1] bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                    <StaticKakaoMapContainer
                                        latitude={placeData.placeLatitude}
                                        longitude={placeData.placeLongitude}
                                        level={3}
                                        className="w-full h-full"
                                    />
                                </div>

                                {/* 장소 정보 - 클릭 가능한 단일 박스 */}
                                <button
                                    onClick={handlePlaceSelect}
                                    className="w-full p-4 bg-gray-50 rounded-lg border border-gray-200 active:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 text-left">
                                            <div className="font-medium text-gray-900 mb-1">{placeData.placeName}</div>
                                            <div className="text-sm text-gray-600 mb-1">{placeData.placeAddress}</div>
                                            {placeData.placePhone && (
                                                <div className="text-sm text-gray-500">{placeData.placePhone}</div>
                                            )}
                                        </div>
                                        <IoChevronForward size={20} className="text-gray-400 ml-3" />
                                    </div>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handlePlaceSelect}
                                className="w-full aspect-[2/1] bg-gray-100 rounded-lg flex flex-col items-center justify-center active:bg-gray-200 transition-colors"
                            >
                                <IoLocationOutline size={32} className="text-gray-400 mb-2" />
                                <p className="text-gray-500 font-medium">장소 선택</p>
                            </button>
                        )}
                    </div>

                    {/* 하단 여백 */}
                    <div className="h-20"></div>
                </div>

                {/* 하단 버튼들 */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                    <div className="max-w-md mx-auto flex space-x-3">
                        <button
                            onClick={handleCancel}
                            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium active:bg-gray-300 transition-colors"
                        >
                            취소
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!isFormValid()}
                            className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                                isFormValid()
                                    ? 'bg-amber-500 text-white active:bg-amber-600'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            수정
                        </button>
                    </div>
                </div>
            </div>
        </CommonLayout>
    );
};

export default MeetingUpdatePage;
